import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  autoEscalationListQuery,
  autoEscalationShellQuery,
  changeAutoEscalationProfileStatus,
  createAutoEscalationProfile,
  deleteAutoEscalationProfile,
  type AutoEscalationProfile,
} from '../../api/settingsEaseedeskAutoEscalation'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { SelectPicker } from '../../ui/SelectPicker'
import { SettingsHead } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Auto escalation settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/escalation/
 * auto_escalation/index.php` + `list.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/escalation/auto_escalation/index-1.js` si
 * `list-1.js`.
 *
 * UN PROFIL = un nume, un GRUP catre care pleaca tichetul, si un NUMAR DE
 * MINUTE dupa care pleaca. Profilul singur nu face nimic: se leaga de o stare a
 * unui tip de tichet in fila "Assigning auto escalation". De-aia se naste
 * STINS - un profil pornit in clipa in care s-a scris ar incepe sa mute tichete
 * inainte sa fie legat de ceva.
 */
function ProfileRow({ profile, onReload }: { profile: AutoEscalationProfile; onReload(): void }) {
  const flip = useMutation({
    mutationFn: (on: boolean) => changeAutoEscalationProfileStatus(profile.ID, on),
    onSuccess: () => {
      toast('Profile status changed', 'Success')
      onReload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => deleteAutoEscalationProfile(profile.ID),
    onSuccess: () => {
      toast('Profile deleted', 'Success')
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <tr>
      <td>{profile.profile_name}</td>
      <td>{profile.group_name}</td>
      <td>
        <label className="label label-primary">{profile.escalation_time} (minutes)</label>
      </td>
      <td style={{ textAlign: 'center' }}>
        <div className="btn-group" role="group" aria-label="controls">
          <button
            type="button"
            className="btn btn-danger btn-xs delete-profile"
            data-profile={profile.ID}
            title="Delete profile"
            aria-label={`Delete profile ${profile.profile_name}`}
            onClick={() => {
              if (!window.confirm('Delete this profile?\nYou will not be able to recover this profile!')) return
              remove.mutate()
            }}
          >
            {/*
              * Semnul era scris cu numele din Font Awesome 4 si fara
              * prefixul de stil care spune din ce familie se ia. Foaia
              * servita e FA 5.15, unde numele acela nu exista: butonul
              * iesea un dreptunghi rosu GOL. `fas fa-trash` e in
              * `public/css/app.css`, cautat acolo inainte de a-l scrie.
              */}
            <i className="fas fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          name="profile_status"
          disabled={flip.isPending}
          title="Activate profile"
          aria-label={`Activate profile ${profile.profile_name}`}
          checked={profile.active === 1}
          onChange={(e) => flip.mutate(e.target.checked)}
        />
      </td>
    </tr>
  )
}

export function AutoEscalationSettingsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(autoEscalationShellQuery())
  const list = useQuery(autoEscalationListQuery())
  const [creating, setCreating] = useState(false)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'auto_escalation_settings'] })
  }

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error)
    return <LoadFailed what="Auto escalation settings" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  return (
    <div>
      <SettingsHead
        strong="Auto escalation"
        rest="settings"
        subtitle={
          <>
            Manage, create or edit <strong>profiles</strong>
          </>
        }
      />

      <div className="row" style={{ marginBottom: 20 }}>
        <div className="col-md-12">
          <button type="button" className="btn btn-default pull-right" onClick={() => setCreating(true)}>
            <i className="fas fa-plus" aria-hidden="true"></i> Create profile
          </button>
        </div>
      </div>

      <div className="list-group">
        {list.error ? (
          <LoadFailed what="The profile list" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Profile name</th>
                <th style={{ textAlign: 'left' }}>Group</th>
                <th style={{ textAlign: 'left' }}>Time</th>
                <th>Actions</th>
                <th>Activate</th>
              </tr>
            </thead>
            <tbody>
              {list.data.profiles.map((profile) => (
                <ProfileRow key={profile.ID} profile={profile} onReload={reload} />
              ))}
              {list.data.profiles.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <div className="alert alert-info" style={{ marginBottom: 0 }}>
                      <i className="fas fa-info-circle" aria-hidden="true"></i> No profiles yet. A profile is a
                      group and a number of minutes; it is born switched off, and starts working once it is tied
                      to a ticket status in &ldquo;Assigning auto escalation&rdquo;.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <ProfileModal
        open={creating}
        departments={shell.data.departments}
        times={shell.data.times}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
    </div>
  )
}

/** `newProfileModal` din macheta: nume, grup, si una din cele opt durate. */
export function ProfileModal({
  open,
  departments,
  times,
  onClose,
  onSaved,
}: {
  open: boolean
  departments: { ID: number; name: string }[]
  times: number[]
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [department, setDepartment] = useState(0)
  const [time, setTime] = useState(0)

  useEffect(() => {
    if (!open) return
    setName('')
    setDepartment(0)
    setTime(0)
  }, [open])

  const save = useMutation({
    mutationFn: () => createAutoEscalationProfile(name.trim(), department, time),
    onSuccess: () => {
      toast('New profile successfully created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="newProfileModal" labelledBy="newProfileModalLabel" open={open} onClose={onClose} title="New profile">
      <form
        id="new-profile-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '' || department < 1 || time < 1) return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="profile-name" className="control-label">
            Profile name
          </label>
          <input
            type="text"
            id="profile-name"
            className="form-control"
            name="profile[name]"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="profile-department" className="control-label">
            Department{' '}
            <i
              className="fas fa-info-circle color-blue font-12"
              title="The department to which the ticket will be escalated."
              aria-hidden="true"
            ></i>
          </label>
          {/*
            * In macheta veche amandoua listele sunt `selectpicker` cu
            * `liveSearch` - carligul lui bootstrap-select, plugin pe care
            * v4 nu-l incarca. Ramasesera doua liste simple, iar cea a
            * grupurilor firmei se rasfoieste la mana. `SelectPicker` e
            * lista zonei, cu `form-control` pe invelis; `<select>`-ul
            * adevarat ramane in pagina, ascuns, cu acelasi `name`.
            */}
          <SelectPicker
            id="profile-department"
            className="form-control"
            name="profile[department]"
            title="Select.."
            value={department > 0 ? String(department) : ''}
            onChange={(value) => setDepartment(Number(value) || 0)}
            options={departments.map((d) => ({ value: String(d.ID), label: d.name }))}
          />
        </div>

        <div className="form-group">
          <label htmlFor="profile-time" className="control-label">
            Time{' '}
            <i
              className="fas fa-info-circle color-blue font-12"
              title="The time after which the ticket will be escalated."
              aria-hidden="true"
            ></i>
          </label>
          {/* `data-subtext="(minutes)"` din macheta: randul mic de dupa numar. */}
          <SelectPicker
            id="profile-time"
            className="form-control"
            name="profile[time]"
            title="Select.."
            value={time > 0 ? String(time) : ''}
            onChange={(value) => setTime(Number(value) || 0)}
            options={times.map((t) => ({ value: String(t), label: String(t), subtext: '(minutes)' }))}
          />
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-profile"
            disabled={name.trim() === '' || department < 1 || time < 1 || save.isPending}
          >
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}
